import type {
  CustomerDocAccessCategory,
  CustomerDocAccessDocument,
  CustomerDocAccessRuleChange,
  CustomerDocumentationAccess,
  DocAccessChoice,
  DocAccessTargetType,
} from "@xop-any-time/shared";

/**
 * The pure half of the customer profile's Documentation tab: the folder tree,
 * the pending changes, and what the customer WILL see once they are saved.
 *
 * It lives outside the component so the rules can be tested without rendering
 * a tree — and because the one rule worth getting exactly right (how a folder
 * rule reaches the documents under it) is portal-api's, mirrored here for an
 * instant preview. The mirror is authoritative NOWHERE: Save sends the
 * choices, and the page re-renders off portal-api's answer.
 */

export type Doc = CustomerDocAccessDocument;
export type Folder = CustomerDocAccessCategory;

/** Pending choices, keyed `Document:9` / `DocumentCategory:3`. An entry exists
 * only while it DIFFERS from what is stored, so its size is the number of
 * unsaved changes. */
export type Pending = ReadonlyMap<string, DocAccessChoice>;

export function targetKey(type: DocAccessTargetType, id: number): string {
  return `${type}:${id}`;
}

// ── The tree ──────────────────────────────────────────────────────────────

export interface FolderNode {
  folder: Folder;
  folders: FolderNode[];
  documents: Doc[];
}

/** Documents filed in no folder the reseller can see — release documentation
 * pinned to a version with no folder, mostly. They have no folder control
 * because there is no folder to put a rule on. */
export interface LooseGroup {
  key: "release" | "other";
  label: string;
  documents: Doc[];
}

export interface DocTree {
  folders: FolderNode[];
  loose: LooseGroup[];
}

const byName = (a: FolderNode, b: FolderNode) =>
  a.folder.name.localeCompare(b.folder.name, undefined, { sensitivity: "base" });
const byTitle = (a: Doc, b: Doc) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" });

/**
 * Folder → sub-folder → documents.
 *
 * A folder whose parent is not in the list is a ROOT (the contract says the
 * client shows it at the top level), and so is any folder only reachable
 * through a parent cycle — bad data should cost a folder its nesting, never
 * its documents.
 */
export function buildTree(access: CustomerDocumentationAccess): DocTree {
  const nodes = new Map<number, FolderNode>();
  for (const folder of access.categories) nodes.set(folder.id, { folder, folders: [], documents: [] });

  const roots: FolderNode[] = [];
  const placed = new Set<number>();
  for (const node of nodes.values()) {
    const parentId = node.folder.parentId;
    const parent = parentId !== null && parentId !== node.folder.id ? nodes.get(parentId) : undefined;
    if (parent) parent.folders.push(node);
    else roots.push(node);
  }
  // Anything a walk from the roots never reaches sits in a cycle: promote it.
  const walk = (node: FolderNode): void => {
    if (placed.has(node.folder.id)) return;
    placed.add(node.folder.id);
    node.folders.forEach(walk);
  };
  roots.forEach(walk);
  for (const node of nodes.values()) {
    if (placed.has(node.folder.id)) continue;
    // Detach it from the cycle before promoting, or it would render twice.
    for (const other of nodes.values()) other.folders = other.folders.filter((f) => f !== node);
    roots.push(node);
    walk(node);
  }

  const release: Doc[] = [];
  const other: Doc[] = [];
  for (const doc of access.documents) {
    const home = doc.categoryId !== null ? nodes.get(doc.categoryId) : undefined;
    if (home) home.documents.push(doc);
    else if (doc.shelf === "release") release.push(doc);
    else other.push(doc);
  }

  const sortNode = (node: FolderNode): void => {
    node.folders.sort(byName);
    node.documents.sort(byTitle);
    node.folders.forEach(sortNode);
  };
  roots.sort(byName);
  roots.forEach(sortNode);

  const loose: LooseGroup[] = [];
  if (release.length > 0) loose.push({ key: "release", label: "Release documentation", documents: release.sort(byTitle) });
  if (other.length > 0) loose.push({ key: "other", label: "Other documents", documents: other.sort(byTitle) });

  return { folders: roots, loose };
}

/** Every document in a folder and all of its sub-folders. */
export function subtreeDocuments(node: FolderNode): Doc[] {
  return [...node.documents, ...node.folders.flatMap(subtreeDocuments)];
}

// ── Choices ───────────────────────────────────────────────────────────────

export function storedChoice(
  access: CustomerDocumentationAccess,
  type: DocAccessTargetType,
  id: number,
): DocAccessChoice {
  const row =
    type === "Document"
      ? access.documents.find((d) => d.id === id)
      : access.categories.find((c) => c.id === id);
  return row?.resellerRule ?? null;
}

/** The choice the control shows: the pending one if there is one, else the
 * stored rule. */
export function currentChoice(
  access: CustomerDocumentationAccess,
  pending: Pending,
  type: DocAccessTargetType,
  id: number,
): DocAccessChoice {
  const key = targetKey(type, id);
  return pending.has(key) ? (pending.get(key) ?? null) : storedChoice(access, type, id);
}

/** A new pending map with `choice` recorded — or DROPPED, when it puts the
 * row back to what is stored (so flipping Share then Default again is not an
 * unsaved change). */
export function withChoice(
  access: CustomerDocumentationAccess,
  pending: Pending,
  type: DocAccessTargetType,
  id: number,
  choice: DocAccessChoice,
): Map<string, DocAccessChoice> {
  const next = new Map(pending);
  const key = targetKey(type, id);
  if (choice === storedChoice(access, type, id)) next.delete(key);
  else next.set(key, choice);
  return next;
}

/** The ONE save's body. */
export function toRuleChanges(pending: Pending): CustomerDocAccessRuleChange[] {
  return [...pending.entries()].map(([key, effect]) => {
    const [type, id] = key.split(":") as [DocAccessTargetType, string];
    return { targetType: type, targetId: Number(id), effect };
  });
}

// ── What the customer will see ────────────────────────────────────────────

/** Where a document's reseller rule comes from, after pending changes: its
 * own, or the NEAREST folder above it that has one (portal-api's distance
 * walk — a document rule beats its folder, a folder beats its parent). */
export type EffectiveRule =
  | { effect: "allow" | "deny"; from: "document" }
  | { effect: "allow" | "deny"; from: "folder"; folder: Folder }
  | null;

export function effectiveRule(
  access: CustomerDocumentationAccess,
  pending: Pending,
  doc: Doc,
): EffectiveRule {
  const own = currentChoice(access, pending, "Document", doc.id);
  if (own !== null) return { effect: own, from: "document" };

  const folders = new Map(access.categories.map((c) => [c.id, c]));
  const seen = new Set<number>();
  let folderId = doc.categoryId;
  while (folderId !== null && !seen.has(folderId)) {
    seen.add(folderId);
    const folder = folders.get(folderId);
    if (!folder) break;
    const choice = currentChoice(access, pending, "DocumentCategory", folder.id);
    if (choice !== null) return { effect: choice, from: "folder", folder };
    folderId = folder.parentId;
  }
  return null;
}

/**
 * "Customer sees it" / "Hidden from customer" — or, in the one case this page
 * cannot know, "Follows XOP's default".
 *
 * That case: the stored verdict came FROM a reseller rule (reason
 * `reseller_allow` / `reseller_deny`) and a pending change takes that rule
 * away. What XOP's own publishing says underneath was never sent, so the page
 * says so instead of guessing; Save asks portal-api and the pill settles.
 */
export type Visibility = "visible" | "hidden" | "default";

export function predictedVisibility(
  access: CustomerDocumentationAccess,
  pending: Pending,
  doc: Doc,
): Visibility {
  // XOP's rule outranks ours, and a document the customer portal does not
  // publish cannot be opened by any rule below XOP.
  if (doc.lockedByXop) return doc.customerVisible ? "visible" : "hidden";
  if (!doc.publishedToCustomers) return "hidden";

  const rule = effectiveRule(access, pending, doc);
  if (rule) return rule.effect === "allow" ? "visible" : "hidden";
  if (doc.reason?.startsWith("reseller_")) return "default";
  return doc.customerVisible ? "visible" : "hidden";
}

// ── Which controls are off ───────────────────────────────────────────────

export const SET_BY_XOP = "Set by XOP";
export const NOT_PUBLISHED = "Not published to customers";

/** Why a document's control is disabled, or null when it is not. */
export function documentLock(doc: Doc): string | null {
  if (doc.lockedByXop) return SET_BY_XOP;
  if (!doc.publishedToCustomers) return NOT_PUBLISHED;
  return null;
}

/** Why a folder's control is disabled: XOP ruled the folder, or nothing under
 * it reaches the customer portal at all (a rule there would change nothing). */
export function folderLock(node: FolderNode): string | null {
  if (node.folder.lockedByXop) return SET_BY_XOP;
  if (!subtreeDocuments(node).some((d) => d.publishedToCustomers)) return NOT_PUBLISHED;
  return null;
}
